import Button from 'components/Button';
import Error from 'components/form/Error';
import Textarea from 'components/form/Inputs/Textarea';
import ActionModal from 'components/Modal/ActionModal';
import ProfileNavigationHead from 'components/profileNavigation/Head';
import Space from 'components/Space';
import UserInformationDisplay from 'components/UserInformation/UserInformationDisplay';
import { MentorshipStatusValues } from 'enums/MentorshipStatus';
import { FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import Mentorship from 'types/Mentorship';
import privateAPI from 'utils/privateAxiosInstance';
import { MenteeDetailsContext } from '.';

const ButtonContainer = styled.div`
  display: flex;
  width: fit-content;
  margin-top: 5rem;
  margin-bottom: 5rem;
`;

const Information: FC = () => {
  const { mentee } = useContext(MenteeDetailsContext);
  const { id } = useParams();
  const { data: mentorship } = useSWR<Mentorship>(
    `/mentorship/mentor/mentee-details/${id}`,
  );

  const [open, setOpen] = useState<boolean>(false);
  const [interruptionMotive, setInterruptionMotive] = useState<string>('');
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setInterruptionMotive(e.target.value);
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [motiveError, setMotiveError] = useState<string | null>(null);
  const handleInterrupt = () => {
    if (!interruptionMotive.trim()) {
      setMotiveError("Ce champ est obligatoire.");
      return;
    }
    setMotiveError(null);
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/mentorship/mentor/interrupt-mentorship/${id}`, {
        interruptionMotive,
      })
      .then(() => {
        mutate('/mentorship/mentor');
        mutate(`/mentorship/mentor/mentee-details/${id}`, {
          ...mentorship,
          status: 'INTERRUPTION_REQUEST_MENTOR',
        } as Mentorship);
        setOpen(false);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <ActionModal
        title="Demande d'interruption"
        color="error"
        open={open}
        close={() => setOpen(false)}
        apply={handleInterrupt}
        applyText="Demander l'interruption du mentorat"
        loading={loading}
      >
        <p>Vous êtes sur le point de demander l'interruption du mentorat.</p>
        <p>Cette demande sera examinée par un administrateur.</p>
        <p>
          Elle pourra donner lieu à l'interruption définitive du mentorat vous
          liant à {mentee?.firstName + ' ' + mentee?.lastName}, votre mentoré
          actuel.
        </p>
        <Space height={0.5} />
        {error && <Error>Une erreur est survenue.</Error>}
        <Space height={0.5} />
        <Textarea
          value={interruptionMotive}
          onChange={handleChange}
          noEnterFireSubmit
          characterLimit={1000}
          placeholder="Quelle est la raison de votre demande ?"
          rows={10}
        />
        {motiveError && <Error>{motiveError}</Error>}
      </ActionModal>
      <ProfileNavigationHead
        title="Informations générales"
        subtitle="Liste les informations générales de votre mentor"
      />
      <UserInformationDisplay variant="mentee" reader="mentor" user={mentee} />
      {mentorship &&
        !(
          [
            'INTERRUPTION_REQUEST_MENTEE',
            'INTERRUPTION_REQUEST_MENTOR',
            'FINISHED_ADMIN',
            'FINISHED_DATE',
            'INTERRUPTED_ADMIN',
            'MENTOR_ACCEPTING',
          ] as MentorshipStatusValues[]
        ).includes(mentorship.status as MentorshipStatusValues) && (
          <ButtonContainer>
            <Button
              color="error"
              variant="outlined"
              onClick={() => setOpen(true)}
            >
              Demander l'interruption du mentorat
            </Button>
          </ButtonContainer>
        )}
    </>
  );
};

export default Information;
